import React, { useState } from 'react';
import { Box, Chip, Menu, MenuItem, Typography } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { mismaUm } from '../../../utils/umVentaDocumento';

interface Props {
  umActual: string;
  ums: string[];
  disabled?: boolean;
  onSelect: (um: string) => void;
}

/**
 * UM con equivalencias: un chip compacto que abre un menú con el resto de UMs.
 * La seleccionada es la de la línea (documento origen o UM base al alta).
 */
const POSLineaUmChips: React.FC<Props> = ({ umActual, ums, disabled, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (ums.length === 0) {
    return (
      <Typography
        variant="caption"
        color={umActual ? 'text.primary' : 'text.secondary'}
        fontWeight={umActual ? 600 : 400}
        noWrap
        title={umActual || undefined}
      >
        {umActual || '—'}
      </Typography>
    );
  }

  const label = umActual?.trim() || ums[0] || '—';
  const puedeAbrir = !disabled && ums.length > 1;

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Chip
        size="small"
        color="primary"
        variant="filled"
        onClick={
          puedeAbrir
            ? (e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }
            : undefined
        }
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.15 }}>
            <Box component="span" sx={{ maxWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label}
            </Box>
            {puedeAbrir ? <ArrowDropDownIcon sx={{ fontSize: '1rem', ml: -0.15 }} /> : null}
          </Box>
        }
        title={puedeAbrir ? `UM actual: ${label}. Tocá para cambiar.` : `UM: ${label}`}
        sx={{
          height: 20,
          maxWidth: 68,
          fontSize: '0.65rem',
          fontWeight: 700,
          cursor: puedeAbrir ? 'pointer' : 'default',
          '& .MuiChip-label': { px: 0.5, display: 'flex' },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { minWidth: 100, py: 0.5 } },
        }}
      >
        {ums.map((um) => {
          const selected = mismaUm(um, umActual);
          return (
            <MenuItem
              key={um}
              selected={selected}
              disabled={selected}
              dense
              onClick={() => {
                handleClose();
                if (!selected) onSelect(um);
              }}
              sx={{ py: 0.75 }}
            >
              <Chip
                size="small"
                label={um}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: selected ? 700 : 500,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default POSLineaUmChips;
